export type RendererLocale = "en" | "ar"

export const en = {
  "navigation.applicationPages": "Application pages",
  "field.restricted": "Restricted value.",
  "list.whyRow": "Why this record: {{key}}",
  "metric.restricted": "Restricted metric result.",
  "metric.noResult": "No accepted metric result.",
  "metric.invalid": "Metric buckets are unavailable: the accepted result has an unsupported shape or number range.",
  "metric.empty": "No buckets in the accepted result.",
  "metric.noGroup": "No group",
  "metric.noCurrency": "No currency",
  "metric.currencyLabel": "{{label}} — {{currency}}",
  "metric.population": "Source population: {{population}}",
  "metric.partial": "Top {{shown}} of {{total}} buckets.",
  "metric.order": "Descending by exact value within each currency; ties retain source order.",
  "metric.scale": "Bars compare absolute values within each currency. Exact values appear above.",
  "field.parentUnavailable": "{{label}}: its parent form is unavailable.",
  "field.outsideRow": "{{label}}: a child-row field outside its grid row.",
  "field.notSet": "Not set",
  "field.optionsUnavailable": "The declared choices are unavailable.",
  "action.inputRecordVersion": "Input {{name}} needs an authoritative canonical record version.",
  "condition.rowUnavailable": "The condition reads a row from {{gridId}}, which is not the current child grid.",
  "grid.waitingParent": "Waiting for {{bindingId}} to identify the parent.",
  "grid.notList": "The owned-row binding did not return a child-row list.",
  "grid.duplicateId": "The child snapshot repeated a stable row id; editing is disabled.",
  "grid.readFailed": "The complete child snapshot could not be read: {{error}}",
  "grid.loadingComplete": "Loading the complete child collection…",
  "grid.noContinuation": "This host cannot read further pages, so the complete child collection is unavailable and editing is disabled.",
  "grid.scanning": "The cell is still scanning; the complete child collection is not available yet.",
  "grid.moreRows": "More child rows may exist; load the complete collection before editing.",
  "grid.incomplete": "The complete child collection is not available yet.",
  "grid.exceedsBound": "The child snapshot exceeds this grid's {{maxRows}}-row bound.",
  "grid.invalidRow": "A child row has no stable id, canonical version, or positive integer ordering value; editing is disabled.",
  "grid.invalidOrder": "The owned rows do not have unique dense positions starting at 1; editing is disabled.",
  "grid.noBinding": "An edit grid has no authoritative owned-row binding.",
  "grid.outsideForm": "This child grid is outside its matching form.",
  "grid.loadComplete": "Load complete child collection",
  "grid.loadCompleteLabel": "{{label}}: Load complete child collection",
  "grid.loadingSaved": "Loading the saved child rows…",
  "grid.changed": "The authoritative child rows changed while this draft had edits.",
  "grid.requirednessUnavailable": "Required fields could not be determined. Save is disabled until their conditions are available.",
  "form.reloadRows": "Discard draft and reload current parent and rows",
  "form.savedRefreshPending": "The save was accepted. Editing is disabled until the saved parent and all child rows are loaded.",
  "form.retrySavedRefresh": "Reload saved parent and rows",
  "grid.count": "{{count}} of {{maxRows}} rows",
  "grid.add": "Add row",
  "grid.keyboard": "Focus a row and press Alt+↑ or Alt+↓ to move it. Positions reflect the proposed order.",
  "grid.removed_one": "{{count}} saved row will be removed.",
  "grid.removed_other": "{{count}} saved rows will be removed.",
  "grid.undoRemoval": "Undo last removal",
  "grid.noSecureId": "This browser cannot generate stable child-row ids, so rows cannot be added.",
  "grid.empty": "{{label}}: 0 rows.",
  "grid.row": "Row {{position}}",
  "grid.position": "Position {{position}}",
  "grid.moveUpLabel": "Move up, row {{position}}",
  "grid.moveDownLabel": "Move down, row {{position}}",
  "grid.removeLabel": "Remove row {{position}}",
  "grid.moveUp": "Move up",
  "grid.moveDown": "Move down",
  "grid.remove": "Remove",
  "form.parentChanged": "The parent record changed while this draft was open. This form keeps its original version for concurrency checks.",
  "form.parentUnavailable": "The current parent record is unavailable or has no valid version. Editing and reload are disabled, and the original concurrency version remains pinned.",
  "form.invalidDraft": "One or more values in this draft are invalid. Submission is blocked until each invalid value is corrected or you discard and reload from the authoritative values.",
  "form.reload": "Discard draft and reload",
  "field.requiredValue": "Enter a value for this required field.",
  "field.invalidNumber": "Enter a number, e.g. 12 or 12.50.",
  "field.noRecord": "No current record",
  "field.outsideForm": "{{label}}: a form input outside its form.",
  "action.undeclared": "Action binding {{bindingId}} is not declared.",
  "action.principalLoading": "Still reading who you are from the cell; try again in a moment.",
  "action.principalFailed": "The host did not say who you are, so nothing was submitted: {{error}}",
  "action.confirm": "Confirm {{label}}",
  "action.confirmFallback": "{{label}}?",
  "action.typeConfirm": "Type {{phrase}} to confirm",
  "common.cancel": "Cancel",
  "action.selectionUnavailable": "This action reads a selection, which this shell does not capture yet.",
  "action.recordUnavailable": "This action reads {{bindingId}}, whose record this placement does not have.",
  "action.draftUnavailable": "This action reads a form draft from outside its form.",
  "action.enabledWhen": "Enabled when {{expression}}: {{reason}}",
  "action.notEnabled": "Not enabled for this record ({{expression}}).",
  "action.accepted": "The cell accepted the action.",
  "navigation.undeclared": "Navigation {{itemId}} is not declared.",
  "navigation.unsupportedDestination": "{{label}}: this destination kind is not rendered yet.",
  "navigation.shownWhen": "Shown when {{expression}}: {{reason}}",
  "navigation.needsParameter": "Needs {{name}}, which this row does not supply",
  "navigation.unavailable": "{{label}}: page navigation is unavailable in this host.",
  "navigation.requires": "Requires {{capability}}",
  "list.select": "Select",
  "list.rowActions": "Row actions",
  "list.awaitingSelection": "Select a row in {{bindingId}} to see its linked records.",
  "list.noLinkedYet": "No linked records read yet; more may exist beyond this traversal's bounds.",
  "list.noneOnPage": "None on this page; more records may exist beyond it.",
  "list.noRecords": "No records you can read.",
  "list.selectRow": "Select {{key}}",
  "list.moreLinked": "More linked records may exist beyond this traversal's bounds.",
  "list.moreRecords": "More records exist beyond this bounded page.",
  "list.stillScanning": "The cell is still scanning; more records may exist.",
  "list.moreMayExist": "More records may exist.",
  "list.continue": "Continue",
  "list.loadMore": "Load more",
  "list.noContinuation": "This host cannot read further pages.",
  "list.nextPageFailed": "The next page could not be read: {{error}}",
  "detail.notVisible": "No {{objectType}} with that key is visible to you.",
  "detail.why": "Why",
  "detail.graph": "Open in graph",
  "form.submitted": "Submitted. The cell accepted the action.",
  "form.forRecord": "For {{key}}",
  "common.close": "Close",
  "form.submitting": "Submitting",
  "field.undeclared": "A field node names an undeclared field.",
  "condition.evaluating": "evaluating",
  "condition.unavailable": "condition unavailable",
  "condition.evaluatingTitle": "Evaluating…",
  "page.invalidProjection": "This page's projection does not match the renderer catalog: {{error}}",
  "page.unsupportedComponent": "No catalog component renders \"{{type}}\".",
  "page.invalidSpec": "invalid spec",
  "error.cannotRead": "You can't read {{what}} in this scope",
  "error.cannotLoad": "Couldn't load {{what}}",
  "bootstrap.missing": "The MCP host did not provide this admitted page's bootstrap data.",
  "bootstrap.invalidJson": "The MCP host did not provide valid bootstrap JSON for this admitted page.",
  "bootstrap.unsupported": "The MCP page bootstrap is not a supported norn.mcp-app/1 document.",
  "bootstrap.invalidScope": "The MCP page bootstrap has no valid scope.",
  "bootstrap.invalidTools": "The MCP page bootstrap has no valid bridge tool names.",
  "bootstrap.missingResources": "The MCP page bootstrap has no admitted page resource map.",
  "bootstrap.invalidDefinitions": "The MCP bootstrap definitions are not a canonical artifact array.",
  "navigation.resourceUnavailable": "This MCP host cannot read admitted page resources for navigation.",
  "navigation.wrongPage": "The host returned a different admitted page than the one requested.",
  "host.refused": "The MCP host refused the tool call.",
  "host.invalidResult": "The MCP host returned an invalid tool result; expected exactly one text block.",
  "navigation.wrongScope": "The target page belongs to a different scope.",
  "navigation.definitionsChanged": "The admitted definitions changed while this MCP App was open; reload the MCP resource and rediscover its bridge tools.",
  "host.noSecureKey": "This host cannot create a secure idempotency key, so the action was not sent.",
  "host.invalidPrincipal": "The host returned an invalid principal result.",
  "host.missingRoot": "MCP app root element is missing.",
  "bootstrap.invalidUri": "The MCP page bootstrap has an invalid resource URI for admitted page {{pageId}}.",
  "bootstrap.incompleteArtifact": "Definition artifact {{index}} is not a complete canonical artifact.",
  "bootstrap.missingWidget": "The admitted widget {{appId}} is not in the bootstrap definitions.",
  "bootstrap.missingPage": "The admitted page {{pageId}} is not in widget {{appId}}.",
  "navigation.noResource": "This host has no registered MCP resource for admitted page {{pageId}}.",
  "navigation.noHtml": "The host returned no HTML for admitted page {{pageId}}.",
  "navigation.missingWidget": "The admitted widget {{appId}} is not in the page resource definitions.",
  "context.required": "{{paramId}} is required",
  "context.text": "{{paramId}} must be text",
  "context.boolean": "{{paramId}} must be Boolean",
  "context.number": "{{paramId}} must be a number",
  "host.connectionFailed": "The MCP host bridge could not connect: {{error}}",
  "host.connecting": "Connecting to MCP host…",
  "page.loading": "Loading admitted page…",
  "page.invalidContext": "This page needs valid context: {{problem}}",
  "navigation.failed": "Page navigation failed: {{error}}",
  "page.renderFailed": "The admitted page could not be rendered: {{error}}",
  "list.traversal": "· via {{linkId}} from {{bindingId}}",
  "condition.loadFailed": "Norn's evaluator could not load: {{error}}",
  "condition.recordUnavailable": "The condition reads {{bindingId}}, whose record is not available here.",
  "condition.draftUnavailable": "The condition reads the {{formId}} draft, which is not open here.",
  "condition.selectionUnavailable": "The condition reads a selection, which this shell does not capture yet.",
  "condition.contextUnavailable": "The condition reads the {{pageId}} page's context, which is not this page.",
  "condition.failed": "Evaluation failed ({{code}}): {{error}}",
  "condition.notBoolean": "The condition did not return a Boolean.",
  "context.integer": "{{paramId}} must be an integer",
  "context.booleanLiteral": "{{paramId}} must be true or false",
  "context.date": "{{paramId}} must be an ISO date (YYYY-MM-DD)",
  "context.timestamp": "{{paramId}} must be an ISO timestamp with offset",
  "context.duration": "{{paramId}} must be an ISO duration",
  "context.jsonNavigation": "{{paramId}} is a JSON parameter, which arrives through navigation, not the address bar",
  "binding.notInstalled": "{{id}} {{version}} is not an installed object type",
  "binding.filtersUnavailable": "This list declares filters or sorting the cell's object read cannot apply yet.",
  "binding.emptyTraversal": "This traversal declares no rows or no depth to follow.",
  "binding.compositeKey": "Composite keys are not readable by key yet.",
  "binding.missingKey": "The detail binding declares no key.",
  "binding.notIdentityKey": "{{propertyId}} is not {{entity}}'s identity key, so it cannot be read by key yet.",
  "binding.invalidKeyLiteral": "The key literal is not text or a number.",
  "binding.requiredParameter": "This page needs the {{paramId}} parameter.",
  "binding.undeclaredDependency": "It reads {{bindingId}}, which this widget does not declare.",
  "binding.dependencyLimit_one": "Its dependencies exceed {{count}} bindings.",
  "binding.dependencyLimit_other": "Its dependencies exceed {{count}} bindings.",
  "binding.otherPageContext": "It reads the {{pageId}} page's context, which is not this page.",
  "binding.queryUndeclared": "{{bindingId}} or page {{pageId}} is not declared",
  "binding.queryAwaitingSelection": "{{bindingId}} waits for a selection in {{sourceBindingId}}",
  "binding.answeredAsDetail": "{{bindingId}} answered as a detail",
  "binding.recordKeyUnavailable": "A key read from another record is not supported at page level yet.",
  "action.notInstalled": "{{actionId}} is not an installed action.",
  "action.inputExpression": "Input {{name}} is mapped by an expression only Norn can evaluate.",
  "action.inputSelection": "Input {{name}} reads a selection, which forms do not capture.",
  "action.inputRecordUndeclared": "Input {{name}} reads a record, but the binding declares none.",
  "action.inputRecordUnavailable": "Input {{name}} needs the {{bindingId}} record at this site, which is not here.",
  "page.undeclaredForm": "form {{formId}} is not declared",
  "page.compositionUnavailable": "composing page {{pageId}} of {{widgetId}} is not rendered yet",
  "page.componentUnavailable": "component kind {{kind}} is not rendered yet",
  "binding.legacyReadUnavailable": "This host cannot serve legacy object reads.",
  "binding.traversalCycle": "This traversal's sources form a cycle.",
  "binding.traversalUnavailable": "Link traversal is unavailable in this host.",
  "binding.contextUnavailable": "The admitted binding context is unavailable in this host.",
  "binding.pageRecordUnavailable": "Binding {{bindingId}} is not available as a current page record.",
  "binding.executorUnavailable": "The canonical binding executor is unavailable in this host.",
  "binding.noCurrentRecord": "Binding {{bindingId}} has no current record.",
  "binding.currentRecordUnavailable": "Binding {{bindingId}} cannot provide a current record.",
  "binding.singleSelectionRequired": "Binding {{bindingId}} must expose a single selection to provide a current record.",
  "binding.multipleRecordsSelected": "Binding {{bindingId}} has multiple selected records; this read needs one current record.",
  "binding.selectedRecordMissing": "The selected record from {{bindingId}} is no longer on its current page.",
  "binding.invalidResponse": "The MCP host returned an invalid binding response.",
  "binding.wrongResponseKind": "The MCP host returned {{kind}} for a {{expectedKind}} binding.",
  "binding.invalidDetailResponse": "The MCP host returned an invalid object_detail binding response.",
  "binding.invalidKindResponse": "The MCP host returned an invalid {{kind}} binding response.",
  "binding.unsupportedResponseKind": "The MCP host returned an unsupported binding response kind {{kind}}.",
  "binding.sourceNotOnPage": "Its source {{bindingId}} is not on this page.",
  "binding.sourceSelectionUnavailable": "Its source list {{bindingId}} declares no row selection to start from.",
  "binding.sourceUnreadable": "Its source {{bindingId}} cannot be read here.",
  "binding.invalidTraversalReturn_one": "This traversal follows {{linkId}} up to {{count}} hops, but the link does not lead back to the type that declares it.",
  "binding.invalidTraversalReturn_other": "This traversal follows {{linkId}} up to {{count}} hops, but the link does not lead back to the type that declares it.",
  "checker.cannotDecompress": "This host cannot decompress the embedded Norn checker.",
  "checker.invalidResult": "Norn returned an invalid evaluation result.",
  "checker.cannotEvaluate": "Norn could not evaluate this condition.",

  "checker.expressionFailed": "The expression could not be evaluated.",
} as const

export const ar = {
  "navigation.applicationPages": "صفحات التطبيق",
  "field.restricted": "قيمة مقيّدة.",
  "list.whyRow": "لماذا هذا السجل: {{key}}",
  "metric.restricted": "نتيجة المقياس مقيّدة.",
  "metric.noResult": "لا توجد نتيجة مقياس مقبولة.",
  "metric.invalid": "مجموعات المقياس غير متاحة: صيغة النتيجة المقبولة أو نطاق أعدادها غير مدعوم.",
  "metric.empty": "لا توجد مجموعات في النتيجة المقبولة.",
  "metric.noGroup": "بدون مجموعة",
  "metric.noCurrency": "بدون عملة",
  "metric.currencyLabel": "{{label}} — {{currency}}",
  "metric.population": "عدد سجلات المصدر: {{population}}",
  "metric.partial": "أعلى {{shown}} من {{total}} مجموعة.",
  "metric.order": "ترتيب تنازلي حسب القيمة الدقيقة داخل كل عملة؛ تحتفظ القيم المتساوية بترتيب المصدر.",
  "metric.scale": "تقارن الأشرطة القيم المطلقة داخل كل عملة. تظهر القيم الدقيقة أعلاه.",
  "field.parentUnavailable": "{{label}}: النموذج الرئيسي غير متاح.",
  "field.outsideRow": "{{label}}: حقل صف فرعي خارج صفه في الجدول.",
  "field.notSet": "غير محدد",
  "field.optionsUnavailable": "الخيارات المحددة غير متاحة.",
  "action.inputRecordVersion": "يتطلب المدخل {{name}} إصدار السجل المعتمد بصيغته المعيارية.",
  "condition.rowUnavailable": "يقرأ الشرط صفًا من {{gridId}}، وهو ليس جدول الصفوف الفرعية الحالي.",
  "grid.waitingParent": "في انتظار {{bindingId}} لتحديد السجل الرئيسي.",
  "grid.notList": "لم يُعد ربط الصفوف التابعة قائمة بالصفوف الفرعية.",
  "grid.duplicateId": "تكرر معرّف صف ثابت في بيانات الصفوف الفرعية؛ تم تعطيل التحرير.",
  "grid.readFailed": "تعذرت قراءة جميع الصفوف الفرعية: {{error}}",
  "grid.loadingComplete": "جارٍ تحميل مجموعة الصفوف الفرعية كاملة…",
  "grid.noContinuation": "لا يستطيع هذا المضيف قراءة صفحات أخرى، لذا لا تتوفر مجموعة الصفوف الفرعية كاملة وتم تعطيل التحرير.",
  "grid.scanning": "لا تزال الخلية تفحص البيانات؛ لم تتوفر مجموعة الصفوف الفرعية كاملة بعد.",
  "grid.moreRows": "قد توجد صفوف فرعية أخرى؛ حمّل المجموعة كاملة قبل التحرير.",
  "grid.incomplete": "لم تتوفر مجموعة الصفوف الفرعية كاملة بعد.",
  "grid.exceedsBound": "تتجاوز بيانات الصفوف الفرعية الحد المسموح لهذا الجدول: {{maxRows}}.",
  "grid.invalidRow": "يفتقد صف فرعي معرّفًا ثابتًا أو إصدارًا معياريًا أو موضعًا بعدد صحيح موجب؛ تم تعطيل التحرير.",
  "grid.invalidOrder": "مواضع الصفوف التابعة ليست فريدة ومتتابعة بدءًا من 1؛ تم تعطيل التحرير.",
  "grid.noBinding": "لا يوجد لجدول التحرير ربط معتمد للصفوف التابعة.",
  "grid.outsideForm": "جدول الصفوف الفرعية هذا خارج النموذج المرتبط به.",
  "grid.loadComplete": "تحميل جميع الصفوف الفرعية",
  "grid.loadCompleteLabel": "{{label}}: تحميل جميع الصفوف الفرعية",
  "grid.loadingSaved": "جارٍ تحميل الصفوف الفرعية المحفوظة…",
  "grid.changed": "تغيرت الصفوف الفرعية المعتمدة أثناء وجود تعديلات في هذه المسودة.",
  "grid.requirednessUnavailable": "تعذّر تحديد الحقول المطلوبة. الحفظ معطّل حتى تتوفر شروطها.",
  "form.reloadRows": "تجاهل المسودة وأعد تحميل السجل الرئيسي والصفوف الحالية",
  "form.savedRefreshPending": "تم قبول الحفظ. التحرير معطّل حتى تحميل السجل الرئيسي المحفوظ وجميع الصفوف الفرعية.",
  "form.retrySavedRefresh": "إعادة تحميل السجل الرئيسي والصفوف المحفوظة",
  "grid.count": "الصفوف: {{count}} من {{maxRows}}",
  "grid.add": "إضافة صف",
  "grid.keyboard": "انقل التركيز إلى صف واضغط Alt+↑ أو Alt+↓ لتحريكه. تعكس المواضع الترتيب المقترح.",
  "grid.removed_one": "سيُحذف صف محفوظ واحد.",
  "grid.removed_other": "سيُحذف {{count}} صف محفوظ.",
  "grid.undoRemoval": "التراجع عن آخر حذف",
  "grid.noSecureId": "لا يستطيع هذا المتصفح إنشاء معرّفات ثابتة للصفوف الفرعية، لذا لا يمكن إضافة صفوف.",
  "grid.empty": "{{label}}: لا توجد صفوف.",
  "grid.row": "الصف {{position}}",
  "grid.position": "الموضع {{position}}",
  "grid.moveUpLabel": "نقل الصف {{position}} لأعلى",
  "grid.moveDownLabel": "نقل الصف {{position}} لأسفل",
  "grid.removeLabel": "حذف الصف {{position}}",
  "grid.moveUp": "نقل لأعلى",
  "grid.moveDown": "نقل لأسفل",
  "grid.remove": "حذف",
  "form.parentChanged": "تغير السجل الرئيسي أثناء فتح هذه المسودة. يحتفظ هذا النموذج بالإصدار الأصلي للتحقق من التعديلات المتزامنة.",
  "form.parentUnavailable": "السجل الرئيسي الحالي غير متاح أو لا يملك إصدارًا صالحًا. تم تعطيل التحرير وإعادة التحميل، مع الاحتفاظ بالإصدار الأصلي للتحقق من التعديلات المتزامنة.",
  "form.invalidDraft": "توجد قيمة غير صالحة أو أكثر في هذه المسودة. لا يمكن الإرسال حتى تصحيحها جميعًا أو تجاهل المسودة وإعادة تحميل القيم المعتمدة.",
  "form.reload": "تجاهل المسودة وأعد التحميل",
  "grid.removed_zero": "لن يُحذف أي صف محفوظ.",
  "grid.removed_two": "سيُحذف صفان محفوظان.",
  "grid.removed_few": "ستُحذف {{count}} صفوف محفوظة.",
  "grid.removed_many": "سيُحذف {{count}} صفًا محفوظًا.",
  "field.requiredValue": "أدخل قيمة لهذا الحقل المطلوب.",
  "field.invalidNumber": "أدخل عددًا، مثل 12 أو 12.50.",
  "field.noRecord": "لا يوجد سجل حالي",
  "field.outsideForm": "{{label}}: حقل إدخال خارج نموذجه.",
  "action.undeclared": "ربط الإجراء {{bindingId}} غير مُعلن.",
  "action.principalLoading": "لا تزال هويتك تُقرأ من الخلية؛ حاول مجددًا بعد قليل.",
  "action.principalFailed": "لم يحدد المضيف هويتك، لذا لم يُرسل شيء: {{error}}",
  "action.confirm": "تأكيد {{label}}",
  "action.confirmFallback": "{{label}}؟",
  "action.typeConfirm": "اكتب {{phrase}} للتأكيد",
  "common.cancel": "إلغاء",
  "action.selectionUnavailable": "يقرأ هذا الإجراء تحديدًا لا تدعمه وحدة التحكم هذه بعد.",
  "action.recordUnavailable": "يقرأ هذا الإجراء {{bindingId}}، ولا يتوفر سجله في هذا الموضع.",
  "action.draftUnavailable": "يقرأ هذا الإجراء مسودة نموذج من خارج النموذج.",
  "action.enabledWhen": "يُفعّل عند {{expression}}: {{reason}}",
  "action.notEnabled": "غير مُفعّل لهذا السجل ({{expression}}).",
  "action.accepted": "قبلت الخلية الإجراء.",
  "navigation.undeclared": "التنقل {{itemId}} غير مُعلن.",
  "navigation.unsupportedDestination": "{{label}}: هذا النوع من الوجهات غير معروض بعد.",
  "navigation.shownWhen": "يُعرض عند {{expression}}: {{reason}}",
  "navigation.needsParameter": "يتطلب {{name}}، وهو غير متوفر في هذا الصف",
  "navigation.unavailable": "{{label}}: التنقل بين الصفحات غير متاح في هذا المضيف.",
  "navigation.requires": "يتطلب {{capability}}",
  "list.select": "تحديد",
  "list.rowActions": "إجراءات الصف",
  "list.awaitingSelection": "حدد صفًا في {{bindingId}} لعرض السجلات المرتبطة به.",
  "list.noLinkedYet": "لم تُقرأ سجلات مرتبطة بعد؛ قد يوجد المزيد خارج حدود هذا الاجتياز.",
  "list.noneOnPage": "لا توجد سجلات في هذه الصفحة؛ قد يوجد المزيد بعدها.",
  "list.noRecords": "لا توجد سجلات يمكنك قراءتها.",
  "list.selectRow": "تحديد {{key}}",
  "list.moreLinked": "قد توجد سجلات مرتبطة أخرى خارج حدود هذا الاجتياز.",
  "list.moreRecords": "توجد سجلات أخرى بعد هذه الصفحة المحدودة.",
  "list.stillScanning": "لا تزال الخلية تفحص؛ قد توجد سجلات أخرى.",
  "list.moreMayExist": "قد توجد سجلات أخرى.",
  "list.continue": "متابعة",
  "list.loadMore": "تحميل المزيد",
  "list.noContinuation": "لا يستطيع هذا المضيف قراءة صفحات إضافية.",
  "list.nextPageFailed": "تعذّرت قراءة الصفحة التالية: {{error}}",
  "detail.notVisible": "لا يظهر لك أي {{objectType}} بهذا المفتاح.",
  "detail.why": "لماذا",
  "detail.graph": "فتح في الرسم",
  "form.submitted": "أُرسل. قبلت الخلية الإجراء.",
  "form.forRecord": "لـ {{key}}",
  "common.close": "إغلاق",
  "form.submitting": "جارٍ الإرسال",
  "field.undeclared": "تشير عقدة حقل إلى حقل غير مُعلن.",
  "condition.evaluating": "جارٍ التقييم",
  "condition.unavailable": "الشرط غير متاح",
  "condition.evaluatingTitle": "جارٍ التقييم…",
  "page.invalidProjection": "إسقاط هذه الصفحة لا يطابق كتالوج العارض: {{error}}",
  "page.unsupportedComponent": "لا يعرض أي مكوّن في الكتالوج \"{{type}}\".",
  "page.invalidSpec": "مواصفات غير صالحة",
  "error.cannotRead": "لا يمكنك قراءة {{what}} في هذا النطاق",
  "error.cannotLoad": "تعذر تحميل {{what}}",
  "bootstrap.missing": "لم يوفر مضيف MCP بيانات تهيئة هذه الصفحة المعتمدة.",
  "bootstrap.invalidJson": "لم يوفر مضيف MCP بيانات تهيئة JSON صالحة لهذه الصفحة المعتمدة.",
  "bootstrap.unsupported": "تهيئة صفحة MCP ليست مستند norn.mcp-app/1 مدعومًا.",
  "bootstrap.invalidScope": "لا تتضمن تهيئة صفحة MCP نطاقًا صالحًا.",
  "bootstrap.invalidTools": "لا تتضمن تهيئة صفحة MCP أسماء صالحة لأدوات الربط.",
  "bootstrap.missingResources": "لا تتضمن تهيئة صفحة MCP خريطة موارد الصفحات المعتمدة.",
  "bootstrap.invalidDefinitions": "تعريفات تهيئة MCP ليست مصفوفة عناصر معيارية.",
  "navigation.resourceUnavailable": "لا يستطيع مضيف MCP هذا قراءة موارد الصفحات المعتمدة للتنقل.",
  "navigation.wrongPage": "أعاد المضيف صفحة معتمدة مختلفة عن الصفحة المطلوبة.",
  "host.refused": "رفض مضيف MCP استدعاء الأداة.",
  "host.invalidResult": "أعاد مضيف MCP نتيجة أداة غير صالحة؛ كان المتوقع كتلة نص واحدة فقط.",
  "navigation.wrongScope": "تنتمي الصفحة المطلوبة إلى نطاق مختلف.",
  "navigation.definitionsChanged": "تغيرت التعريفات المعتمدة أثناء فتح تطبيق MCP هذا؛ أعد تحميل مورد MCP واكتشف أدوات الربط مجددًا.",
  "host.noSecureKey": "لا يستطيع هذا المضيف إنشاء مفتاح آمن لمنع التكرار، لذا لم يُرسل الإجراء.",
  "host.invalidPrincipal": "أعاد المضيف نتيجة غير صالحة للجهة المصادَق عليها.",
  "host.missingRoot": "عنصر الجذر لتطبيق MCP غير موجود.",
  "bootstrap.invalidUri": "تتضمن تهيئة صفحة MCP عنوان مورد غير صالح للصفحة المعتمدة {{pageId}}.",
  "bootstrap.incompleteArtifact": "عنصر التعريف {{index}} ليس عنصرًا معياريًا مكتملًا.",
  "bootstrap.missingWidget": "الواجهة المعتمدة {{appId}} غير موجودة في تعريفات التهيئة.",
  "bootstrap.missingPage": "الصفحة المعتمدة {{pageId}} غير موجودة في الواجهة {{appId}}.",
  "navigation.noResource": "لا يوجد لدى هذا المضيف مورد MCP مسجل للصفحة المعتمدة {{pageId}}.",
  "navigation.noHtml": "لم يُعد المضيف HTML للصفحة المعتمدة {{pageId}}.",
  "navigation.missingWidget": "الواجهة المعتمدة {{appId}} غير موجودة في تعريفات مورد الصفحة.",
  "context.required": "{{paramId}} مطلوب",
  "context.text": "يجب أن يكون {{paramId}} نصًا",
  "context.boolean": "يجب أن يكون {{paramId}} قيمة منطقية",
  "context.number": "يجب أن يكون {{paramId}} عددًا",
  "host.connectionFailed": "تعذر اتصال جسر مضيف MCP: {{error}}",
  "host.connecting": "جارٍ الاتصال بمضيف MCP…",
  "page.loading": "جارٍ تحميل الصفحة المعتمدة…",
  "page.invalidContext": "تحتاج هذه الصفحة إلى سياق صالح: {{problem}}",
  "navigation.failed": "فشل التنقل بين الصفحات: {{error}}",
  "page.renderFailed": "تعذر عرض الصفحة المعتمدة: {{error}}",
  "list.traversal": "· عبر {{linkId}} من {{bindingId}}",
  "condition.loadFailed": "تعذّر تحميل مقيّم Norn: {{error}}",
  "condition.recordUnavailable": "يقرأ الشرط {{bindingId}}، ولا يتوفر سجله هنا.",
  "condition.draftUnavailable": "يقرأ الشرط مسودة {{formId}}، وهي غير مفتوحة هنا.",
  "condition.selectionUnavailable": "يقرأ الشرط تحديدًا لا تدعمه وحدة التحكم هذه بعد.",
  "condition.contextUnavailable": "يقرأ الشرط سياق الصفحة {{pageId}}، وهي ليست هذه الصفحة.",
  "condition.failed": "فشل التقييم ({{code}}): {{error}}",
  "condition.notBoolean": "لم يُرجع الشرط قيمة منطقية.",
  "context.integer": "يجب أن يكون {{paramId}} عددًا صحيحًا",
  "context.booleanLiteral": "يجب أن يكون {{paramId}} إما true أو false",
  "context.date": "يجب أن يكون {{paramId}} تاريخًا بصيغة ISO (YYYY-MM-DD)",
  "context.timestamp": "يجب أن يكون {{paramId}} طابعًا زمنيًا بصيغة ISO مع إزاحة زمنية",
  "context.duration": "يجب أن يكون {{paramId}} مدة بصيغة ISO",
  "context.jsonNavigation": "{{paramId}} مُعامِل JSON يُمرَّر عبر التنقل، وليس شريط العنوان",
  "binding.notInstalled": "{{id}} {{version}} ليس نوع كائن مثبّتًا",
  "binding.filtersUnavailable": "تُعلِن هذه القائمة تصفية أو ترتيبًا لا تستطيع قراءة الكائنات في الخلية تطبيقه بعد.",
  "binding.emptyTraversal": "لا يُعلِن هذا الاجتياز صفوفًا أو عمقًا يمكن اتباعه.",
  "binding.compositeKey": "قراءة المفاتيح المركّبة بالمفتاح غير مدعومة بعد.",
  "binding.missingKey": "لا يُعلِن ربط التفاصيل أي مفتاح.",
  "binding.notIdentityKey": "{{propertyId}} ليس مفتاح هوية {{entity}}، لذا لا يمكن قراءته بالمفتاح بعد.",
  "binding.invalidKeyLiteral": "القيمة الحرفية للمفتاح ليست نصًا أو عددًا.",
  "binding.requiredParameter": "تتطلب هذه الصفحة المُعامِل {{paramId}}.",
  "binding.undeclaredDependency": "يقرأ {{bindingId}}، وهو ربط لا تُعلِنه هذه الواجهة.",
  "binding.dependencyLimit_zero": "تتجاوز تبعياته {{count}} روابط.",
  "binding.dependencyLimit_one": "تتجاوز تبعياته ربطًا واحدًا.",
  "binding.dependencyLimit_two": "تتجاوز تبعياته ربطين.",
  "binding.dependencyLimit_few": "تتجاوز تبعياته {{count}} روابط.",
  "binding.dependencyLimit_many": "تتجاوز تبعياته {{count}} ربطًا.",
  "binding.dependencyLimit_other": "تتجاوز تبعياته {{count}} ربط.",
  "binding.otherPageContext": "يقرأ سياق الصفحة {{pageId}}، وهي ليست هذه الصفحة.",
  "binding.queryUndeclared": "الربط {{bindingId}} أو الصفحة {{pageId}} غير مُعلَن",
  "binding.queryAwaitingSelection": "ينتظر الربط {{bindingId}} تحديدًا في {{sourceBindingId}}",
  "binding.answeredAsDetail": "أعاد الربط {{bindingId}} استجابة تفاصيل",
  "binding.recordKeyUnavailable": "قراءة مفتاح من سجل آخر غير مدعومة على مستوى الصفحة بعد.",
  "action.notInstalled": "{{actionId}} ليس إجراءً مثبّتًا.",
  "action.inputExpression": "المدخل {{name}} مربوط بتعبير لا يمكن تقييمه إلا بواسطة Norn.",
  "action.inputSelection": "يقرأ المدخل {{name}} تحديدًا لا تدعمه النماذج.",
  "action.inputRecordUndeclared": "يقرأ المدخل {{name}} سجلًا، لكن الربط لا يُعلِن أي سجل.",
  "action.inputRecordUnavailable": "يتطلب المدخل {{name}} سجل الربط {{bindingId}} في هذا الموضع، وهو غير موجود هنا.",
  "page.undeclaredForm": "النموذج {{formId}} غير مُعلَن",
  "page.compositionUnavailable": "تكوين الصفحة {{pageId}} من الواجهة {{widgetId}} غير معروض بعد",
  "page.componentUnavailable": "نوع المكوّن {{kind}} غير معروض بعد",
  "binding.legacyReadUnavailable": "لا يستطيع هذا المضيف توفير قراءات الكائنات القديمة.",
  "binding.traversalCycle": "تشكّل مصادر هذا الاجتياز دورة.",
  "binding.traversalUnavailable": "اجتياز الروابط غير متاح في هذا المضيف.",
  "binding.contextUnavailable": "سياق الربط المعتمد غير متاح في هذا المضيف.",
  "binding.pageRecordUnavailable": "الربط {{bindingId}} غير متاح كسجل حالي للصفحة.",
  "binding.executorUnavailable": "منفّذ الربط المعتمد غير متاح في هذا المضيف.",
  "binding.noCurrentRecord": "لا يوجد سجل حالي للربط {{bindingId}}.",
  "binding.currentRecordUnavailable": "لا يستطيع الربط {{bindingId}} توفير سجل حالي.",
  "binding.singleSelectionRequired": "يجب أن يدعم الربط {{bindingId}} تحديدًا واحدًا لتوفير سجل حالي.",
  "binding.multipleRecordsSelected": "يتضمن الربط {{bindingId}} عدة سجلات محدّدة؛ تتطلب هذه القراءة سجلًا حاليًا واحدًا.",
  "binding.selectedRecordMissing": "لم يعد السجل المحدّد من {{bindingId}} موجودًا في صفحته الحالية.",
  "binding.invalidResponse": "أعاد مضيف MCP استجابة ربط غير صالحة.",
  "binding.wrongResponseKind": "أعاد مضيف MCP النوع {{kind}} لربط من النوع {{expectedKind}}.",
  "binding.invalidDetailResponse": "أعاد مضيف MCP استجابة ربط object_detail غير صالحة.",
  "binding.invalidKindResponse": "أعاد مضيف MCP استجابة ربط {{kind}} غير صالحة.",
  "binding.unsupportedResponseKind": "أعاد مضيف MCP نوع استجابة ربط غير مدعوم {{kind}}.",
  "binding.sourceNotOnPage": "مصدره {{bindingId}} ليس في هذه الصفحة.",
  "binding.sourceSelectionUnavailable": "لا تُعلِن قائمته المصدر {{bindingId}} تحديدًا لصف يبدأ منه الاجتياز.",
  "binding.sourceUnreadable": "لا يمكن قراءة مصدره {{bindingId}} هنا.",
  "binding.invalidTraversalReturn_zero": "يتبع هذا الاجتياز الرابط {{linkId}} حتى {{count}} خطوات، لكن الرابط لا يعود إلى النوع الذي يُعلِنه.",
  "binding.invalidTraversalReturn_one": "يتبع هذا الاجتياز الرابط {{linkId}} حتى خطوة واحدة، لكن الرابط لا يعود إلى النوع الذي يُعلِنه.",
  "binding.invalidTraversalReturn_two": "يتبع هذا الاجتياز الرابط {{linkId}} حتى خطوتين، لكن الرابط لا يعود إلى النوع الذي يُعلِنه.",
  "binding.invalidTraversalReturn_few": "يتبع هذا الاجتياز الرابط {{linkId}} حتى {{count}} خطوات، لكن الرابط لا يعود إلى النوع الذي يُعلِنه.",
  "binding.invalidTraversalReturn_many": "يتبع هذا الاجتياز الرابط {{linkId}} حتى {{count}} خطوة، لكن الرابط لا يعود إلى النوع الذي يُعلِنه.",
  "binding.invalidTraversalReturn_other": "يتبع هذا الاجتياز الرابط {{linkId}} حتى {{count}} خطوة، لكن الرابط لا يعود إلى النوع الذي يُعلِنه.",
  "checker.cannotDecompress": "لا يستطيع هذا المضيف فك ضغط فاحص Norn المضمَّن.",
  "checker.invalidResult": "أعادت Norn نتيجة تقييم غير صالحة.",
  "checker.cannotEvaluate": "تعذّر على Norn تقييم هذا الشرط.",

  "checker.expressionFailed": "تعذّر تقييم التعبير.",
} as const satisfies Record<keyof typeof en, string> & Record<string, string>

export type RendererMessageKey = keyof typeof en | { [K in keyof typeof en]: K extends `${infer Base}_one` | `${infer Base}_other` ? Base : never }[keyof typeof en]

/** Stored copy stays semantic until rendering; external text stays verbatim. */
export type Notice<Key extends string = RendererMessageKey> =
  | { readonly key: Key; readonly values?: Readonly<Record<string, string | number | undefined>>; readonly issue?: number }
  | { readonly text: string }

export function formatNotice<Key extends string>(notice: Notice<Key> | readonly Notice<Key>[], translate: (key: Key, values?: Readonly<Record<string, string | number | undefined>>) => string, separator = " "): string {
  return (Array.isArray(notice) ? notice as readonly Notice<Key>[] : [notice as Notice<Key>])
    .map((n) => "key" in n ? `${translate(n.key, n.values)}${n.issue ? ` (#${n.issue})` : ""}` : n.text)
    .filter(Boolean)
    .join(separator)
}

/** English messages are for logs; callers render the retained notice in their current locale. */
export class NoticeError<Key extends string = RendererMessageKey> extends Error {
  constructor(readonly notice: Notice<Key>, translate: (key: Key, values?: Readonly<Record<string, string | number | undefined>>) => string) {
    super(formatNotice(notice, translate))
    this.name = "NoticeError"
  }
}

export function noticeOf(error: unknown): Notice {
  return error instanceof NoticeError ? error.notice : { text: error instanceof Error ? error.message : String(error) }
}

export function rendererNotice(notice: Notice | readonly Notice[], locale: string, separator = " "): string {
  return formatNotice(notice, (key, values) => message(locale, key, values), separator)
}

/** Locale lookup, CLDR plurals and the catalogs' {{name}} interpolation convention. */
export function message(locale: string, key: RendererMessageKey, vars: Readonly<Record<string, string | number | undefined>> = {}): string {
  const language: RendererLocale = locale.toLowerCase().startsWith("ar") ? "ar" : "en"
  const catalog: Readonly<Record<string, string>> = language === "ar" ? ar : en
  const count = vars.count
  const form = typeof count === "number" ? new Intl.PluralRules(language).select(count) : undefined
  const template = (form ? catalog[`${key}_${form}`] ?? catalog[`${key}_other`] : undefined) ?? catalog[key] ?? key
  return template.replace(/{{(\w+)}}/g, (placeholder, name: string) => vars[name] === undefined ? placeholder : String(vars[name]))
}
